import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Play, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

// Shared read-only preview shell. Renders coverage / leave / fairness
// preview payloads with distinct visualisations. NO writes.

const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const ruleId = (kind, rule) => kind === "coverage" ? rule.coverage_id
  : kind === "leave" ? rule.leave_rule_id : rule.fairness_id;

const today = () => new Date().toISOString().slice(0, 10);
const plusDays = (iso, n) => {
  const d = new Date(iso); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export default function PolicyPreviewDialog({ open, onOpenChange, kind, rule }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  // coverage inputs
  const [covWindow, setCovWindow] = useState("week");
  const [covDate, setCovDate] = useState(today());
  // leave inputs
  const [leaveStart, setLeaveStart] = useState(today());
  const [leaveEnd, setLeaveEnd] = useState(plusDays(today(), 4));
  const [leaveType, setLeaveType] = useState("annual");
  // fairness inputs
  const [fairStart, setFairStart] = useState(plusDays(today(), -29));
  const [fairEnd, setFairEnd] = useState(today());

  useEffect(() => {
    if (!open) return;
    setResult(null);
    setCovWindow("week");
    setCovDate(today());
    setLeaveStart(today());
    setLeaveEnd(plusDays(today(), 4));
    setLeaveType("annual");
    setFairStart(plusDays(today(), -29));
    setFairEnd(today());
  }, [open, rule]);

  if (!rule || !kind) return null;
  const rid = ruleId(kind, rule);

  const run = async () => {
    setBusy(true);
    try {
      let payload = {};
      let path = "";
      if (kind === "coverage") {
        path = `/coverage-rules/${rid}/preview`;
        payload = { on_date: covDate, window: covWindow };
      } else if (kind === "leave") {
        path = `/leave-rules/${rid}/preview`;
        payload = { start_date: leaveStart, end_date: leaveEnd, leave_type: leaveType };
      } else if (kind === "fairness") {
        path = `/fairness-rules/${rid}/preview`;
        payload = { window_start: fairStart, window_end: fairEnd };
      }
      const { data } = await api.post(path, payload);
      setResult(data);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Preview failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" fullscreenOnMobile data-testid="policy-preview-dialog">
        <DialogHeader>
          <DialogTitle>Preview: {rule.name}</DialogTitle>
          <DialogDescription>
            Read-only dry-run. Nothing is written. Used to validate the policy
            against current data without affecting live scheduling.
          </DialogDescription>
        </DialogHeader>

        {/* Inputs vary by kind */}
        {kind === "coverage" && (
          <div className="flex items-end gap-3 mb-3 flex-wrap">
            <div className="flex-1 min-w-[140px]">
              <Label className="eyebrow">From date</Label>
              <Input type="date" value={covDate} data-testid="cov-preview-date"
                onChange={(e) => setCovDate(e.target.value)}
                className="h-10 rounded-lg mt-2" />
            </div>
            <div className="min-w-[140px]">
              <Label className="eyebrow">Window</Label>
              <Select value={covWindow} onValueChange={setCovWindow}>
                <SelectTrigger className="h-10 rounded-lg mt-2"
                  data-testid="cov-preview-window"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Day</SelectItem>
                  <SelectItem value="week">Week</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <RunButton onClick={run} busy={busy} testid="policy-preview-run" />
          </div>
        )}
        {kind === "leave" && (
          <div className="flex items-end gap-3 mb-3 flex-wrap">
            <div className="flex-1 min-w-[140px]">
              <Label className="eyebrow">Start</Label>
              <Input type="date" value={leaveStart} data-testid="leave-preview-start"
                onChange={(e) => setLeaveStart(e.target.value)}
                className="h-10 rounded-lg mt-2" />
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="eyebrow">End</Label>
              <Input type="date" value={leaveEnd} data-testid="leave-preview-end"
                onChange={(e) => setLeaveEnd(e.target.value)}
                className="h-10 rounded-lg mt-2" />
            </div>
            <div className="min-w-[140px]">
              <Label className="eyebrow">Type</Label>
              <Select value={leaveType} onValueChange={setLeaveType}>
                <SelectTrigger className="h-10 rounded-lg mt-2"
                  data-testid="leave-preview-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["annual", "sick", "unpaid", "bank_holiday", "other"].map((t) =>
                    <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <RunButton onClick={run} busy={busy} testid="policy-preview-run" />
          </div>
        )}
        {kind === "fairness" && (
          <div className="flex items-end gap-3 mb-3 flex-wrap">
            <div className="flex-1 min-w-[140px]">
              <Label className="eyebrow">Window start</Label>
              <Input type="date" value={fairStart} data-testid="fair-preview-start"
                onChange={(e) => setFairStart(e.target.value)}
                className="h-10 rounded-lg mt-2" />
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="eyebrow">Window end</Label>
              <Input type="date" value={fairEnd} data-testid="fair-preview-end"
                onChange={(e) => setFairEnd(e.target.value)}
                className="h-10 rounded-lg mt-2" />
            </div>
            <RunButton onClick={run} busy={busy} testid="policy-preview-run" />
          </div>
        )}

        {result && (
          <div data-testid="policy-preview-result" className="max-h-96 overflow-auto">
            {kind === "coverage" && <CoverageResult d={result} />}
            {kind === "leave" && <LeaveResult d={result} />}
            {kind === "fairness" && <FairnessResult d={result} />}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RunButton({ onClick, busy, testid }) {
  return (
    <Button type="button" onClick={onClick} disabled={busy}
      data-testid={testid}
      className="h-10 rounded-lg bg-[#2563EB] hover:bg-[#1D4ED8]">
      <Play className="h-4 w-4 mr-1" />{busy ? "Running…" : "Run preview"}
    </Button>
  );
}

function CoverageResult({ d }) {
  return (
    <>
      <div className="flex gap-2 mb-3 text-xs">
        <span data-testid="cov-summary-gap"
              className="px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
          {d.summary.days_with_gap} days with gap
        </span>
        <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
          Max gap: {d.summary.max_gap}
        </span>
        <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
          {d.summary.window_start} → {d.summary.window_end}
        </span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#F6F9FE] sticky top-0">
          <tr className="text-left text-[#4B5773]">
            <th className="py-2 px-3 eyebrow font-normal">Date</th>
            <th className="py-2 px-3 eyebrow font-normal">Day</th>
            <th className="py-2 px-3 eyebrow font-normal">In window</th>
            <th className="py-2 px-3 eyebrow font-normal">Min / Max</th>
            <th className="py-2 px-3 eyebrow font-normal">Assigned</th>
            <th className="py-2 px-3 eyebrow font-normal">On leave</th>
            <th className="py-2 px-3 eyebrow font-normal">Gap</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {d.days.map((day) => (
            <tr key={day.date} data-testid={`cov-day-${day.date}`}
                className={day.gap > 0 ? "bg-amber-50/40" : ""}>
              <td className="py-2 px-3">{day.date}</td>
              <td className="py-2 px-3 text-[#4B5773]">{DAY_NAMES[day.day_of_week]}</td>
              <td className="py-2 px-3">{day.in_window ? "Yes" : "-"}</td>
              <td className="py-2 px-3">{day.min_headcount ?? "-"} / {day.max_headcount ?? "-"}</td>
              <td className="py-2 px-3">{day.assigned_headcount}</td>
              <td className="py-2 px-3">{day.leave_headcount}</td>
              <td className="py-2 px-3 font-medium">
                {day.gap > 0
                  ? <span className="text-amber-700">{day.gap}</span>
                  : <span className="text-emerald-700">0</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function LeaveResult({ d }) {
  return (
    <div data-testid="leave-preview-result">
      <div className="flex items-center gap-2 mb-3">
        {d.would_block ? (
          <span data-testid="leave-block" className="inline-flex items-center text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border bg-red-50 text-red-700 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />Would block
          </span>
        ) : (
          <span data-testid="leave-allow" className="inline-flex items-center text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />Would allow
          </span>
        )}
        {d.would_warn && (
          <span className="inline-flex items-center text-[11px] uppercase tracking-widest font-medium px-2 py-0.5 rounded-full border bg-amber-50 text-amber-700 border-amber-200">
            <AlertTriangle className="h-3 w-3 mr-1" />Has warnings
          </span>
        )}
      </div>
      {d.reasons.length === 0 && d.warnings.length === 0 && (
        <p className="text-sm text-emerald-700">All checks passed.</p>
      )}
      {d.reasons.map((r, i) => (
        <div key={`r-${i}`} className="text-sm text-red-700">• {r.message}</div>
      ))}
      {d.warnings.map((w, i) => (
        <div key={`w-${i}`} className="text-sm text-amber-700 flex items-center gap-1">
          <AlertTriangle className="h-3.5 w-3.5" />{w.message}
        </div>
      ))}
    </div>
  );
}

function FairnessResult({ d }) {
  return (
    <>
      <div className="flex gap-2 mb-3 text-xs">
        <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
          Mean: {d.mean}
        </span>
        <span data-testid="fair-outside-count"
              className="px-2 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
          {d.summary.outside_tolerance} outside tolerance
        </span>
        <span className="px-2 py-1 rounded-full bg-slate-100 text-slate-600 border border-slate-200">
          {d.summary.total_employees} total
        </span>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-[#F6F9FE] sticky top-0">
          <tr className="text-left text-[#4B5773]">
            <th className="py-2 px-3 eyebrow font-normal">Employee</th>
            <th className="py-2 px-3 eyebrow font-normal">Value</th>
            <th className="py-2 px-3 eyebrow font-normal">Gap</th>
            <th className="py-2 px-3 eyebrow font-normal">Deviation</th>
            <th className="py-2 px-3 eyebrow font-normal">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {d.results.map((row) => (
            <tr key={row.employee_id} data-testid={`fair-row-${row.employee_id}`}
                className={row.outside_tolerance ? "bg-red-50/40" : ""}>
              <td className="py-2 px-3 font-medium text-[#0B1A36]">{row.employee_name}</td>
              <td className="py-2 px-3">{row.value}</td>
              <td className="py-2 px-3">{row.gap_from_mean}</td>
              <td className="py-2 px-3">{row.deviation_pct == null ? "-" : `${row.deviation_pct}%`}</td>
              <td className="py-2 px-3">
                {row.outside_tolerance
                  ? <span className="text-red-700 text-xs">Outside</span>
                  : <span className="text-emerald-700 text-xs">Within</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
